import compact from "lodash/compact";
import React, { FunctionComponent, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../../hooks/useCustomizationContext";
import { ReactComponent as IconColor } from "../../../../../icons/16x16/color-picker.svg";
import { ReactComponent as IconLayout } from "../../../../../icons/16x16/layout.svg";
import { Panel } from "../../../../../states/customization";
import { TemplateColorDefinition } from "../../../../../types/customization/template";
import { evalContentCondition } from "../../../../../utils/customization/condition";
import { isDesktopSize } from "../../../../../utils/customization/device";
import {
  findColorDefinition,
  findPage,
  findPageAndDefinition,
} from "../../../../../utils/customization/finders";
import { canSelectPageDefinitions } from "../../../../../utils/customization/pageDefinition";
import CoverLayouts from "../../../../molecules/customization/CoverLayouts";
import EditionPanelTabs from "../../../../molecules/customization/panels/EditionPanelTabs";
import OptionList from "../../../../molecules/customization/panels/OptionList";
import SelectionContent from "../PageEditionPanel/SelectionContent";
import "./style.scss";

interface Props {
  elementDefinitionKey?: string;
  openTab?: "photos" | "styles";
  pageKey: string;
}

const CoverPanel: FunctionComponent<Props> = ({
  elementDefinitionKey,
  openTab = "styles",
  pageKey,
}) => {
  const { content, dispatch, template } = useCustomizationContext();
  const intl = useIntl();

  const [panelUndoKey] = useState<string>(`coverPanel${uuid()}`);

  const hasLayouts = canSelectPageDefinitions(template, content, { type: "front-cover" });
  const currentPage = findPage(pageKey, content);

  const { page, pageDefinition } = useMemo(
    () => findPageAndDefinition(pageKey, template, content),
    [content, pageKey, template]
  );

  // get definitions of back-cover and spine we should update with front cover
  const relatedDefinitions = template.pageDefinitions.filter(
    (definition) =>
      (definition.type === "spine" || definition.type === "back-cover") &&
      evalContentCondition(definition.conditions || [], template, content)
  );
  const relatedDefinitionKeys = relatedDefinitions.map((definition) => definition.key);
  const relatedPages = content.pages.filter(({ pageDefinitionKey }) =>
    relatedDefinitionKeys.includes(pageDefinitionKey)
  );

  const handleSelectColor = (colorKey: string) => {
    dispatch({ type: "setPageToFocus", pageKey });

    dispatch({
      colorDefinitionKey: colorKey,
      pageKey,
      type: "changePageColorDefinition",
      undoKey: panelUndoKey,
    });
    for (const relatedPage of relatedPages) {
      dispatch({
        colorDefinitionKey: colorKey,
        pageKey: relatedPage.key,
        type: "changePageColorDefinition",
        undoKey: panelUndoKey,
      });
    }
  };

  const colorItems = useMemo(() => {
    if (!page || !pageDefinition || !isDesktopSize() || hasLayouts) {
      return null;
    }

    let backgroundKeys = pageDefinition.backgroundColorKeys;
    if (relatedPages.length > 0) {
      backgroundKeys = relatedDefinitions.reduce((finalkeys: string[], relatedDefinition) => {
        return finalkeys.filter((key) => relatedDefinition.backgroundColorKeys.includes(key));
      }, backgroundKeys);
    }
    if (backgroundKeys.length < 2) {
      return null;
    }

    let selectedColors: TemplateColorDefinition | undefined;

    const colorValuesFormatted = backgroundKeys.map((colorKey) => {
      const colorDefinition = findColorDefinition(colorKey, template.colorDefinitions);
      if (!colorDefinition) {
        return null;
      }

      if (colorDefinition.key === page.backgroundColorKey) {
        selectedColors = colorDefinition;
      }

      return {
        key: colorDefinition.key,
        onSelect: () => handleSelectColor(colorDefinition.key),
        value: colorDefinition.value,
      };
    });

    return (
      <OptionList
        icon={<IconColor />}
        optionItems={compact(colorValuesFormatted)}
        selectedValue={selectedColors && selectedColors.key}
        title={intl.formatMessage({ id: "kustom_OptionsPanel_color" })}
        type="color"
      />
    );
  }, [content, dispatch, intl, page, pageKey, pageDefinition, panelUndoKey, template]);

  const handleClickOnTabs = (selectedTab: "photos" | "styles") => {
    dispatch({ key: pageKey, openedTab: selectedTab, panel: Panel.EDITCOVER, type: "setPanel" });
  };

  return (
    <div className="CoverPanel">
      <EditionPanelTabs disableStyles={!hasLayouts} onClick={handleClickOnTabs} openTab={openTab} />
      <div className="CoverPanel-content">
        {openTab === "photos" && (
          <SelectionContent elementDefinitionKey={elementDefinitionKey} pageKey={pageKey} />
        )}

        {openTab === "styles" && (
          <>
            {hasLayouts && (
              <OptionList
                icon={<IconLayout />}
                optionItems={[]}
                title={intl.formatMessage({
                  id: "kustom_PageEditionPanel_layout",
                })}
                type="layout"
              >
                <CoverLayouts
                  currentPageDefinitionKey={currentPage && currentPage.pageDefinitionKey}
                  onColorChange={handleSelectColor}
                  pageKey={pageKey}
                  undoKey={panelUndoKey}
                />
              </OptionList>
            )}

            {colorItems}
          </>
        )}
      </div>
    </div>
  );
};

export default CoverPanel;
